import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import TopBar from 'components/TopBar';
import SafeAreaView from 'components/SafeAreaView';
import { useNavigation } from 'hooks/useNavigation';

type TLayoutContext = {
  headerTitle?: ReactNode;
  changeTitle: (title: ReactNode) => void;
};

export const LayoutContext = createContext<TLayoutContext>(
  {} as TLayoutContext
);

export const LayoutProvider: React.FC = ({ children }) => {
  const [title, setTitle] = useState<ReactNode>('');
  const [closable, setClosable] = useState(true);

  useEffect(() => {
    function handleShouldClose(e: CustomEventInit) {
      setClosable(e.detail);
    }
    window.addEventListener('shouldClose', handleShouldClose);

    return () => {
      window.removeEventListener('shouldClose', handleShouldClose);
    };
  }, []);

  const {
    goBack,
    isRootPage: shouldClose,
    options: { headerRight: HeaderRight },
    closeApp,
  } = useNavigation();

  const changeTitle = (t: ReactNode) => setTitle(t);

  const isRootPage = shouldClose || closable;

  const buttonAction = isRootPage ? closeApp : goBack;

  const currentValue = useMemo<TLayoutContext>(() => {
    return {
      changeTitle,
    };
  }, [changeTitle]);

  return (
    <LayoutContext.Provider value={currentValue}>
      <SafeAreaView>
        <TopBar
          title={title}
          isRootPage={isRootPage}
          onActionButtonClick={buttonAction}
          rightContent={HeaderRight}
        />
        {children}
      </SafeAreaView>
    </LayoutContext.Provider>
  );
};

export function useLayout(): TLayoutContext {
  return useContext(LayoutContext);
}
